import u from 'umbrellajs';
import videojs from 'video.js';
import { play } from './play';

export interface AudioLayer {
    setSource(context: string, source: string | null): Promise<void>;
    setActive(active: boolean, afterFade?: () => void): void;
    isActive(): boolean;
}

export class DummyAudioLayerClass {
    setSource(_context: string, _source: string): Promise<void> {
        return new Promise((_resolve) => {});
    }
    setActive(_active: boolean, _afterFade?: () => void): void {}
    isActive(): boolean {
        return false;
    }
}

export class AudioLayerClass {
    ref: HTMLElement;
    source: URL | null = null;
    token: string = '';
    zindex: number;
    player: any = null;
    active: boolean = false;
    context: string = '';
    name: string = 'audio';

    constructor(ref: HTMLElement, zindex: number) {
        this.ref = ref;
        this.zindex = zindex;

        this.ref.style.zIndex = zindex + '';
        this.ref.style.opacity = '0';
    }

    async sleep(ms: number): Promise<void> {
        return new Promise((resolve) => setTimeout(resolve, ms));
    }

    getPlayerOptions(): any {
        const options = {
            id: 'player_' + this.context,
            controls: false,
            autoplay: true,
            preload: 'metadata',
            muted: false,
            fluid: true,
        };

        return options;
    }

    async onPlayerState(state: string, description: string): Promise<void> {
        if (state === 'error') {
            console.error(this.context, state, description);

            await this.sleep(5000);

            this.loadPlayer();
        }
    }

    unloadPlayer(): void {
        if (this.player !== null) {
            this.player.dispose();
            this.player = null;
        }

        u(this.ref).empty();
    }

    loadPlayer(): void {
        if (this.player !== null) {
            this.player.dispose();
            this.player = null;
        }

        u(this.ref)
            .empty()
            .append(
                `<div class="video-wrapper"><video-js id="${this.name}-video-wrapper"></video-js></div>`,
            );

        console.log(
            this.context,
            'initializing audio player',
            this.getPlayerOptions(),
        );

        try {
            const player = videojs(
                `${this.name}-video-wrapper`,
                this.getPlayerOptions(),
                () => {
                    console.log(
                        this.context,
                        'player ready',
                        player.currentSrc(),
                    );

                    player.on('loadedmetadata', async () => {
                        play(this.context, this.name, player);

                        console.log(
                            this.context,
                            `player ready: zindex=${this.zindex}`,
                        );
                    });

                    player.on('playing', () => {
                        // Player ist playing
                        this.onPlayerState('playing', 'all good');
                        if (this.active) {
                            this.player.volume(1);
                        } else {
                            this.player.volume(0);
                        }
                    });

                    player.on('pause', () => {
                        this.onPlayerState('error', 'player stopped');
                    });

                    player.tech_.on('retryplaylist', () => {
                        // retryplaylist ist Error
                        this.onPlayerState('error', 'loading playlist');
                    });

                    player.on('error', () => {
                        // error ist Error
                        this.onPlayerState('error', 'other error');
                    });
                },
            );

            player.on('xhr-hooks-ready', async () => {
                const playerXhrRequestHook = (options: any) => {
                    let u = new URL(options.uri);
                    u.searchParams.set('token', this.token);

                    options.uri = u.toString();
                    return options;
                };

                while (!player.tech(true)) {
                    await this.sleep(100);
                }

                if ((player.tech(true) as any).vhs) {
                    (player.tech(true) as any).vhs.xhr.onRequest(
                        playerXhrRequestHook,
                    );
                }
            });
            this.player = player;

            if (this.source) {
                this.player.src(this.source.toString());
                play(this.context, this.name, this.player);
            }
        } catch (error) {
            // Some errors can't be caught. Chrome may prevent playback because of energy limits.
            this.onPlayerState('error', `creating player failed: ${error}`);
        }
    }

    async setSource(context: string, source: string): Promise<void> {
        this.context = context;

        if (source.length == 0) {
            this.source = null;

            if (!this.player) {
                return;
            }

            const performFadeOut = (volume: number) => {
                volume = Math.max(volume - 0.1, 0);

                this.player.volume(volume);

                if (volume > 0) {
                    setTimeout(performFadeOut, 100, volume);
                } else {
                    this.unloadPlayer();
                }
            };

            // Startet den Fade-Out Effekt
            performFadeOut(1);

            return;
        }

        if (this.source == null) {
            try {
                this.source = new URL(source);
            } catch (error) {
                console.error(this.context, `parsing url: ${source}: ${error}`);
                return;
            }

            let token = this.source.searchParams.get('token');
            if (token) {
                this.token = token;
            } else {
                this.token = '';
            }

            await this.sleep(1000);

            this.loadPlayer();

            return;
        }

        // Check the new source URL. If it is the same but the token parameter, just update the token.
        // Otherwise replace the source URL and load it into the player.

        let u = new URL(source);

        let token = u.searchParams.get('token');
        if (token) {
            this.token = token;
        } else {
            this.token = '';
        }

        if (u.host == this.source.host && u.pathname == this.source.pathname) {
            console.log(this.context, 'setting new token', this.token);
            this.source.searchParams.set('token', this.token);

            if (!this.player) {
                this.loadPlayer();
            }
            return;
        }

        console.log(this.context, 'setting new source', source);

        this.source = u;

        if (!this.player) {
            this.loadPlayer();
            return;
        }

        const performFadeOut = (volume: number) => {
            volume = Math.max(volume - 0.1, 0);

            this.player.volume(volume);

            if (volume > 0) {
                setTimeout(performFadeOut, 100, volume);
            } else {
                this.player.src(u.toString());
            }
        };

        // Startet den Fade-Out Effekt
        performFadeOut(1);
    }

    setActive(active: boolean, afterFade?: () => void): void {
        if (!this.player) {
            return;
        }

        if (active) {
            this.player.volume(1);
        } else {
            this.player.volume(0);
        }

        this.active = active;

        if (afterFade) {
            afterFade();
        }
    }

    isActive(): boolean {
        return this.active;
    }
}
